import { useCallback, useEffect, useRef, useState } from 'react';

import { Paragraph2, Select } from 'inpock-ui-react';

import { Option, Option as OptionValue, Campaign, SubOption, CampaignV2 } from '@/apis';
import { Box } from '@/components/common';

import { TempCartData } from '../OptionSelectSheet';

import * as S from './styled';

interface OptionSelectorProps {
  campaign: CampaignV2;
  onComplete: (cart: TempCartData) => void;
}

class OptionNotFound extends Error {}
class SubOptionNotFound extends Error {}

const useSelectedOption = (campaign: Campaign) => {
  const [selectedOption, setSelectedOption] = useState<OptionValue>();

  const resetSelectedOption = () => {
    setSelectedOption(undefined);
  };

  useEffect(() => {
    resetSelectedOption();
  }, [campaign.id]);

  return { selectedOption, setSelectedOption, resetSelectedOption };
};

const getOption = (campaign: Campaign, optionId: Option['id']): Option => {
  const option = campaign.options.find((option) => option.id == optionId);
  if (option) {
    return option;
  } else {
    throw new OptionNotFound();
  }
};

const getSubOption = (option: Option | undefined, id: SubOption['id']): SubOption => {
  if (option === undefined) {
    throw new OptionNotFound();
  }

  const subOption = option.sub_options.find((option) => option.id === id);
  if (subOption) {
    return subOption;
  } else {
    throw new SubOptionNotFound();
  }
};

export function OptionSelector({ campaign, onComplete }: OptionSelectorProps) {
  const { selectedOption, setSelectedOption, resetSelectedOption } = useSelectedOption(campaign);
  const mainOptionSelector = useRef<HTMLSelectElement & { reset: () => void }>(null);
  const subOptionSelector = useRef<HTMLSelectElement & { reset: () => void }>(null);
  const additionalProductSelectorRefs = useRef<{
    [key: string]: (HTMLSelectElement & { reset: () => void }) | null;
  }>({});

  const setRef = (key: string, element: (HTMLSelectElement & { reset: () => void }) | null) => {
    additionalProductSelectorRefs.current = {
      ...additionalProductSelectorRefs.current,
      [key]: element,
    };
  };

  // 상위 옵션 품절 여부
  const isMainOptionSoldout = useCallback(
    (option: Option) => {
      if (!!option.sub_options.length) {
        const allSubOptionsSoldOut = option.sub_options.every(
          (subOption) => subOption.stock_quantity === 0,
        );

        return allSubOptionsSoldOut || campaign.is_sold_out;
      } else {
        return option.stock_quantity === 0 || campaign.is_sold_out;
      }
    },
    [campaign.is_sold_out],
  );

  // 하위 옵션 품절 여부
  const isSubOptionSoldout = useCallback(
    (option: Option | SubOption) => {
      return option.stock_quantity === 0 || campaign.is_sold_out;
    },
    [campaign.is_sold_out],
  );

  // 상위 옵션 목록
  // 하위 옵션까지 체크해서 상위 옵션의 품절 여부를 체크해야 함
  const mainOptions = campaign.options.map((option) => {
    const optionLabel =
      option.name +
      (isMainOptionSoldout(option)
        ? ' (품절)'
        : campaign.price - option.price !== 0 && !option.sub_options.length
        ? ` (${option.price - campaign.price >= 0 ? '+' : ''}${(
            option.price - campaign.price
          ).toLocaleString()}원)`
        : '');
    return {
      value: option.id.toString(),
      label: optionLabel,
      disabled: isMainOptionSoldout(option),
    };
  });

  // 하위 옵션 목록
  const subOptions = selectedOption?.sub_options.map((option) => {
    const subOptionLabel =
      option.name +
      (isSubOptionSoldout(option)
        ? ' (품절)'
        : campaign.price - option.price !== 0
        ? ` (${option.price - campaign.price >= 0 ? '+' : ''}${(
            option.price - campaign.price
          ).toLocaleString()}원)`
        : '');
    return {
      value: option.id.toString(),
      label: subOptionLabel,
      disabled: isSubOptionSoldout(option),
    };
  });

  const emitComplete = (cart: TempCartData) => {
    onComplete(cart);
    resetSelectedOption();
    if (mainOptionSelector.current) {
      mainOptionSelector.current.value = '';
      mainOptionSelector.current?.reset();
    }

    if (subOptionSelector.current) {
      subOptionSelector.current.value = '';
      subOptionSelector.current?.reset();
    }
  };

  const onClickOption = (id: number) => {
    const option = getOption(campaign, id);

    const isComplete = option.sub_options.length === 0;

    if (!isComplete) {
      setSelectedOption(option);
      return;
    }

    if (isComplete) {
      const cart: TempCartData = {
        campaign,
        option,
        stock_quantity: 1,
      };
      emitComplete(cart);

      return;
    }
  };

  const onClickSubOption = (id: number) => {
    const subOption = getSubOption(selectedOption, id);

    const cart: TempCartData = {
      campaign,
      option: selectedOption,
      sub_option: subOption,
      stock_quantity: 1,
    };

    emitComplete(cart);
  };

  const onClickAdditionalProduct = (name: string, id: number) => {
    if (!campaign.additional_products) return;

    const additionalProductOption = campaign.additional_products
      .find((product) => product.options.find((option) => option.id === id))
      ?.options.find((option) => option.id === id);

    if (!additionalProductOption) {
      return;
    }

    const additionalProduct = {
      id: additionalProductOption.id,
      additional_product_name: name,
      option_name: additionalProductOption.option_name,
      price: additionalProductOption.price,
      stock_quantity: additionalProductOption.stock_quantity,
    };

    const cart: TempCartData = {
      campaign,
      additional_product: additionalProduct,
      stock_quantity: 1,
    };

    if (additionalProductSelectorRefs.current[name]) {
      additionalProductSelectorRefs.current[name]?.reset();
    }

    emitComplete(cart);
  };

  return (
    <Box>
      {mainOptions.length > 0 && (
        <>
          <Paragraph2 className="pl-12 mb-8">본품</Paragraph2>
          <S.StyledSelect
            ref={mainOptionSelector}
            className="main-option-selector"
            options={mainOptions}
            active={!!selectedOption}
            defaultOption={campaign.main_option_name}
            onInput={(e) => onClickOption(Number(e))}
            value=""
            style={{
              width: '100%',
              padding: '0 12px',
              marginBottom: '8px',
            }}
          />
          {campaign.sub_option_name &&
            (selectedOption ? selectedOption.sub_options.length > 0 : true) && (
              <Select
                ref={subOptionSelector}
                options={subOptions || []}
                defaultOption={campaign.sub_option_name}
                onInput={(e) => onClickSubOption(Number(e))}
                value=""
                style={{ width: '100%', padding: '0 12px', marginBottom: '8px' }}
              />
            )}
        </>
      )}
      {campaign.additional_products && campaign.additional_products.length > 0 && (
        <>
          <Paragraph2 className="pl-12 mt-30 mb-8">추가상품</Paragraph2>
          {campaign.additional_products.map((product) => {
            return (
              <S.StyledSelect
                key={product.name}
                ref={(element) =>
                  setRef(
                    product.name,
                    element as (HTMLSelectElement & { reset: () => void }) | null,
                  )
                }
                options={product.options.map((option) => ({
                  value: option.id.toString(),
                  label:
                    option.option_name +
                    ` (+${option.price}원)` +
                    (option.stock_quantity === 0 ? ' (품절)' : ''),
                  disabled: option.stock_quantity === 0,
                }))}
                defaultOption={product.name}
                onInput={(id) => onClickAdditionalProduct(product.name, Number(id))}
                value=""
                style={{
                  width: '100%',
                  padding: '0 12px',
                  marginBottom: '8px',
                }}
              />
            );
          })}
        </>
      )}
    </Box>
  );
}
